import { type FeatureContext, type ProtectedAtom } from '../types';

import { resolveNavigationTarget } from './navigation-target';
import {
	exceedsCodePoints,
	getTerminalPunctuation,
	hasNonLatinLetter,
	normalizeQuery,
} from './normalize';
import { findProtectedAtoms } from './protected-atoms';
import { segmentWordTokens } from './word-tokens';

/**
 * One feature context per classification — see `FeatureContext` for why every derived
 * feature is a lazy, memoized getter.
 *
 * The memo cell records `computed` separately from `value` so a feature whose answer is
 * `false` is still only computed once.
 */

type Memo<T> = { computed: boolean; value: T | undefined };

const memo = <T>(): Memo<T> => ({ computed: false, value: undefined });

const read = <T>(cache: Memo<T>, compute: () => T): T => {
	if (!cache.computed) {
		cache.value = compute();
		cache.computed = true;
	}

	return cache.value as T;
};

export const createFeatureContext = (input: string): FeatureContext => {
	const qMatch = normalizeQuery(input);

	const exceedsCodePointLimit = memo<boolean>();
	const wordTokens = memo<{ tokens: readonly string[]; exceededCap: boolean }>();
	const protectedAtoms = memo<readonly ProtectedAtom[]>();
	const nonLatin = memo<boolean>();
	const terminalPunctuation = memo<string>();
	const navigationTarget = memo<boolean>();

	return {
		qMatch,

		get exceedsCodePointLimit(): boolean {
			return read(exceedsCodePointLimit, () => exceedsCodePoints(qMatch));
		},

		get wordTokens(): readonly string[] {
			return read(wordTokens, () => segmentWordTokens(qMatch)).tokens;
		},

		get exceededWordTokenCap(): boolean {
			return read(wordTokens, () => segmentWordTokens(qMatch)).exceededCap;
		},

		get protectedAtoms(): readonly ProtectedAtom[] {
			return read(protectedAtoms, () => findProtectedAtoms(qMatch));
		},

		get hasNonLatinLetter(): boolean {
			return read(nonLatin, () => hasNonLatinLetter(qMatch));
		},

		get terminalPunctuation(): string {
			return read(terminalPunctuation, () => getTerminalPunctuation(qMatch));
		},

		get navigationTarget(): boolean {
			return read(navigationTarget, () => resolveNavigationTarget(qMatch));
		},
	};
};
